import { MailOutlineOutlined, PhoneOutlined, PlaceOutlined } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";

import { CONTACT, OFFICES } from "../templates/offices-canonical";
import { colors, palette, typeScale } from "../theme/tokens";

import type { ReactNode } from "react";

// Our offices and contact details at the foot of "Who we are" (Bill,
// 2026-09-08): one static 1px-line card per office (photo, city, what the
// office is, the address), then "Get in touch with us": the phone and the
// general inbox as two info-tinted callout cards.
export function Offices() {
    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
                Our offices
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", ...typeScale.lead, mb: 3, maxWidth: 1064 }}>
                Supplier partnerships are led from Hinsdale. Engineering, marketplace operations and support run from Kaunas.
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 3,
                }}
            >
                {OFFICES.map((o) => (
                    <Box
                        key={o.city}
                        sx={{
                            border: 1,
                            borderColor: colors.line,
                            borderRadius: "10px",
                            bgcolor: "background.paper",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            component="img"
                            src={o.photoUrl}
                            alt={o.photoAlt}
                            loading="lazy"
                            sx={{ display: "block", width: "100%", aspectRatio: "16 / 10", objectFit: "cover" }}
                        />
                        <Box sx={{ p: 2.5 }}>
                            <Typography variant="overline" component="p" sx={{ mb: 0.25 }}>
                                {o.label}
                            </Typography>
                            <Typography sx={{ fontSize: "1.0625rem", fontWeight: 700, color: colors.navy }}>
                                {o.city}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.25 }}>
                                <PlaceOutlined sx={{ fontSize: 18, color: colors.mediumGray, mt: "2px" }} />
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                    {o.address.map((line) => (
                                        <Box component="span" key={line} sx={{ display: "block" }}>
                                            {line}
                                        </Box>
                                    ))}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>
                ))}
            </Box>
            <Typography variant="h4" sx={{ mt: 5, mb: 2 }}>
                Get in touch with us
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 3,
                }}
            >
                <Callout icon={<PhoneOutlined />} label="Call us" href={CONTACT.phone.href} value={CONTACT.phone.display} />
                <Callout icon={<MailOutlineOutlined />} label="Email us" href={`mailto:${CONTACT.email}`} value={CONTACT.email} />
            </Box>
        </Box>
    );
}

// An info-tinted callout card (the brand's info callout, no coloured edge):
// icon, a small label, the value as the link.
function Callout({ icon, label, href, value }: { icon: ReactNode; label: string; href: string; value: string }) {
    return (
        <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ bgcolor: palette.infoBg, borderRadius: "10px", px: 2.5, py: 2.25 }}
        >
            <Box sx={{ color: colors.navy, display: "flex", "& > svg": { fontSize: 24 } }}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {label}
                </Typography>
                <Typography component="a" href={href} sx={{ fontWeight: 700, fontSize: "1.0625rem", wordBreak: "break-all" }}>
                    {value}
                </Typography>
            </Box>
        </Stack>
    );
}
